import {useState, useEffect, useRef} from 'react';

import type {ProductProps} from '@/molecules/Cards/Product/typings';
import type {Props} from './typings';

import {
  fetchProductsBySlugs,
  formatFullProductToProductProps,
  getAbortControllers,
  getErrorMessage,
  getSlugsToFetch,
} from './utils';

/**
 * @description Custom hook to manage abort controllers for fetching products by their slugs.
 * - It creates a set of abort controllers for each slug and returns the signals and controllers.
 * - It cleans up the controllers on unmount to prevent memory leaks.
 * @param slugs Array of product slugs to fetch.
 * @return {object} An object containing the abort signals and controllers.
 */
const useAbortControllers = (slugs: string[]) => {
  const abortControllers = useRef<ReturnType<typeof getAbortControllers>>(
    getAbortControllers(slugs),
  );

  useEffect(() => {
    return () => {
      Object.values(abortControllers.current[0]).forEach((controller) => controller.abort());
    };
  }, [slugs]);

  return {signals: abortControllers.current[1], controllers: abortControllers.current[0]};
};

interface FetchedDataState {
  data: ProductProps[];
  status: 'idle' | 'loading' | 'success' | 'error' | 'fetchMore';
  error: null | string;
}

/**
 * @description Custom hook to fetch products by their slugs.
 * - Fetches products based on the provided slugs and display size units.
 * - Returns the fetched products, their status, and any error that occurred during the fetch.
 * @param {object} param - props object containing `slugs` and `displaySizeUnits`.
 * @return {FetchedDataState} An object containing the fetched products, their status, and any error.
 */
const useProductsBySlugs = ({slugs, displaySizeUnits, size = 4}: Props) => {
  const [products, setProducts] = useState<FetchedDataState>({
    data: [],
    status: 'idle',
    error: null,
  });
  const {signals: abortSignals} = useAbortControllers(slugs);
  /**
   * @description Keep track of already fetched slugs to avoid duplicate requests.
   */
  const alreadyFetchedSlugs = useRef(new Set<string>());
  const mountedRef = useRef(false);

  const slugsToString = slugs.join(',');

  const fetchProducts = async (currentSlugs: string[]) => {
    try {
      setProducts((current) => ({
        ...current,
        status: 'loading',
        error: null,
      }));

      alreadyFetchedSlugs.current = new Set([...alreadyFetchedSlugs.current, ...currentSlugs]);
      const fullProducts = await fetchProductsBySlugs({
        slugs: currentSlugs,
        abortSignals,
      });

      if (!mountedRef.current) return;

      const filteredProducts = fullProducts
        .filter((product) => !!product.quantity)
        .map((fullProduct) => formatFullProductToProductProps(fullProduct, displaySizeUnits));

      setProducts((current) => {
        const newData = [...current.data, ...filteredProducts];
        let newStatus: FetchedDataState['status'] = current.status;

        if (newData.length >= size) {
          /**
           * @description If we have enough products, set status to 'success'.
           */
          newStatus = 'success';
        } else {
          /**
           * @description If we don't have enough products, set status to 'fetchMore'.
           */
          newStatus = 'fetchMore';
        }

        return {
          data: newData,
          status: newStatus,
          error: null,
        };
      });
    } catch (err) {
      if (!mountedRef.current) return;

      setProducts({
        data: [],
        status: 'error',
        error: getErrorMessage(err),
      });
    }
  };

  /**
   * @description Effect to handle component mount and unmount.
   * - Sets the mountedRef to true on mount.
   * - Cleans up the mountedRef on unmount to prevent state updates after unmount.
   */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * @description Fetches products by their slugs and filters out the unavailable ones.
   * - IF the `slugs` array is empty, it DOES NOT fetch and returns early.
   * - IF the status is error, loading or success, it DOES NOT fetch and returns early.
   * - If the `status` is 'idle' or 'fetchMore',the effect will run,
   * but will only fetch or refetch by slugs that haven't been fetched yet.
   * - These are high-latency API calls, so it uses abort controllers to cancel on unmount.
   */
  useEffect(() => {
    if (slugs.length === 0) return;
    if (products.status !== 'fetchMore' && products.status !== 'idle') return;

    const currentSlugs = getSlugsToFetch({
      slugs,
      alreadyFetched: alreadyFetchedSlugs.current,
      take: size,
    });

    fetchProducts(currentSlugs);
  }, [slugsToString, products.status]);

  return {
    data: products.data ? products.data.slice(0, size) : [],
    status: products.status,
    error: products.error,
  };
};

export default useProductsBySlugs;
